// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';

import type { UserSession } from '@/app/user-context';
import { exchangeCode } from '@/auth/oidc';

const POST_LOGIN_PATH_KEY = 'goffy:post-login-path';

export const useAuthCallback = (params: {
  code?: string;
  state?: string;
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
  onError?: (message: string) => void;
}) => {
  const { code, state, user, setUser, onError } = params;
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const exchangeRef = useRef<{ code: string | null; inFlight: boolean }>({ code: null, inFlight: false });

  useEffect(() => {
    const run = async () => {
      if (user) return;
      if (!code) {
        onError?.('Saknar code från Keycloak.');
        return;
      }
      if (exchangeRef.current.inFlight || exchangeRef.current.code === code) {
        return;
      }
      exchangeRef.current = { code, inFlight: true };
      try {
        setLoading(true);
        const result = await exchangeCode(code, state);
        setUser({
          id: result.id,
          forename: result.name,
          token: result.accessToken,
          idToken: result.idToken ?? null,
          refreshToken: result.refreshToken ?? null,
        });
        const storedReturnTo =
          typeof window !== 'undefined' ? window.sessionStorage.getItem(POST_LOGIN_PATH_KEY) : null;
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
        }
        router.replace(storedReturnTo || '/tournaments');
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Token-utbyte misslyckades');
        exchangeRef.current = { code, inFlight: false };
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [code, state, onError, router, setUser, user]);

  return { loading };
};
